'use client';

/**
 * Hero image carousel — sits at the very top of the home page, above the
 * brand text hero. Pulls prompts that have a cover image (or a first image
 * in their gallery), then dissolves through them every ~5s.
 *
 * Design choices:
 *   - **Crossfade** transition (`opacity` between stacked layers) rather
 *     than a horizontal slide. Crossfades feel more cinematic for full-
 *     bleed images and don't fight the rest of the home page (which already
 *     has a horizontal slide carousel further down).
 *   - **Subtle Ken Burns zoom** on the active slide via a CSS animation —
 *     the image starts at 1.0 scale and slowly zooms to 1.06 over the slide
 *     duration. Stops the banner from feeling static.
 *   - **Caption card** sits at the bottom-left with category eyebrow, serif
 *     title, author + stats, and an "Open prompt" CTA. Card has a frosted
 *     backdrop so it stays legible over any image.
 *   - **Auto-advance**, paused on hover/focus and on tab visibility hidden
 *     (no point burning CPU when the user isn't looking).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { api } from '@/lib/api';
import { Prompt } from '@/types';
import PromptDetailModal from '@/components/prompt/PromptDetailModal';

const AUTO_ADVANCE_MS = 5500;
const FETCH_SIZE = 24;
const TARGET_SLIDES = 8;

const CATEGORY_DOT: Record<string, string> = {
  Writing:   'oklch(0.75 0.14 160)',
  Coding:    'oklch(0.72 0.14 230)',
  Art:       'oklch(0.72 0.15 285)',
  Business:  'oklch(0.78 0.14 75)',
  Education: 'oklch(0.75 0.14 40)',
  Other:     'oklch(0.58 0.008 85)',
};

function pickImage(p: Prompt): string | null {
  return p.cover_image_url || (Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : null);
}

export default function HeroImageCarousel() {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [openPrompt, setOpenPrompt] = useState<Prompt | null>(null);
  const initRef = useRef(false);

  // Fetch order:
  //   1. Random shuffle of Art-category prompts (the visually rich ones)
  //   2. If that returns fewer than 3, top up with random image-bearing
  //      prompts from any category so the banner never feels empty
  //   3. Banner self-hides if literally no image-bearing prompts exist
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const collect = async () => {
      try {
        // Step 1 — random Art prompts.
        const art = await api.prompts.list({
          category: 'Art', sort: 'random', size: FETCH_SIZE,
        });
        const artWithImages = art.items.filter((p) => !!pickImage(p));

        if (artWithImages.length >= 3) {
          setPrompts(artWithImages.slice(0, TARGET_SLIDES));
          return;
        }

        // Step 2 — top up with other random image-bearing prompts. Keep Art
        // first so the banner still leads with the most visual category.
        const fallback = await api.prompts.list({
          sort: 'random', size: FETCH_SIZE,
        });
        const fallbackWithImages = fallback.items.filter(
          (p) => !!pickImage(p) && p.category !== 'Art',
        );
        const combined = [...artWithImages, ...fallbackWithImages].slice(0, TARGET_SLIDES);
        setPrompts(combined);
      } catch {
        /* silent — banner stays hidden */
      }
    };
    void collect();
  }, []);

  const len = prompts.length;

  // Auto-advance.
  useEffect(() => {
    if (paused || len <= 1) return;
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % len);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [paused, len]);

  // Pause when the tab is backgrounded — saves bandwidth and avoids the
  // "I came back and it was on slide 47" surprise.
  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const next = useCallback(() => setActive((i) => (i + 1) % Math.max(1, len)), [len]);
  const prev = useCallback(() => setActive((i) => (i - 1 + len) % Math.max(1, len)), [len]);

  // Hide entirely until we have at least one image-bearing prompt.
  if (len === 0) return null;

  const current = prompts[active];
  const dotColor = CATEGORY_DOT[current.category ?? 'Other'] ?? CATEGORY_DOT.Other;

  return (
    <>
      <section
        aria-label="Featured prompt banner"
        aria-roledescription="carousel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="hero-image-carousel"
        style={{
          position: 'relative',
          width: '100%',
          height: 'clamp(320px, 52vh, 540px)',
          overflow: 'hidden',
          background: 'var(--bg-soft)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        {/* Stacked image layers — only the active one is opacity:1 */}
        {prompts.map((p, i) => {
          const src = pickImage(p);
          if (!src) return null;
          return (
            <div
              key={p.id}
              aria-hidden={i !== active}
              style={{
                position: 'absolute', inset: 0,
                opacity: i === active ? 1 : 0,
                transition: 'opacity 1.2s ease',
                pointerEvents: i === active ? 'auto' : 'none',
                overflow: 'hidden',
              }}
            >
              <div
                key={`${i === active ? 'on' : 'off'}-${i}`}  // restart Ken-Burns on re-activate
                style={{
                  position: 'absolute', inset: 0,
                  animation: i === active ? 'hero-kenburns 6s ease-out forwards' : 'none',
                }}
              >
                <Image
                  src={src}
                  alt=""
                  fill
                  priority={i === 0}
                  sizes="100vw"
                  style={{ objectFit: 'cover' }}
                  unoptimized
                />
              </div>
              {/* Bottom gradient — keeps the caption card legible */}
              <div style={{
                position: 'absolute', inset: 0,
                background:
                  'linear-gradient(180deg, oklch(0.05 0.005 260 / 0.05) 0%, oklch(0.05 0.005 260 / 0.15) 45%, oklch(0.05 0.005 260 / 0.65) 100%)',
              }} />
            </div>
          );
        })}

        {/* Caption card — pinned bottom-left over the gradient. Frosted backdrop
            so the card stays readable regardless of the image's busy spots. */}
        <button
          type="button"
          onClick={() => setOpenPrompt(current)}
          className="page-pad"
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            padding: '0 var(--page-pad-x, 48px) 28px',
            display: 'flex', justifyContent: 'flex-start',
            background: 'transparent', border: 0, cursor: 'pointer',
            color: '#fff', textAlign: 'left',
            font: 'inherit',
          }}
        >
          <div
            style={{
              maxWidth: 640,
              padding: '18px 22px 18px',
              background: 'rgba(8, 10, 18, 0.55)',
              backdropFilter: 'blur(12px) saturate(1.2)',
              WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
              border: '1px solid rgba(255, 255, 255, 0.14)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 10px 30px -10px rgba(0,0,0,0.45)',
              transition: 'transform 0.25s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'rgba(255, 255, 255, 0.86)',
              marginBottom: 8,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: dotColor, flexShrink: 0,
              }} />
              {current.category ?? 'Other'}
              <span aria-hidden style={{ opacity: 0.45, margin: '0 4px' }}>·</span>
              Featured
            </div>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontVariationSettings: '"opsz" 110, "SOFT" 50',
              fontSize: 'clamp(26px, 3.6vw, 42px)',
              fontWeight: 400,
              letterSpacing: '-0.025em',
              lineHeight: 1.05,
              margin: '0 0 10px',
              color: '#fff',
              textWrap: 'balance',
            }}>
              {current.title}
            </h2>
            {current.description && (
              <p style={{
                fontSize: 14,
                lineHeight: 1.45,
                color: 'rgba(255, 255, 255, 0.82)',
                margin: '0 0 12px',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}>
                {current.description}
              </p>
            )}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              fontSize: 12.5, color: 'rgba(255, 255, 255, 0.78)',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {current.user.avatar_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={current.user.avatar_url}
                    alt=""
                    width={20}
                    height={20}
                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)',
                    display: 'grid', placeItems: 'center',
                    fontSize: 10, fontWeight: 600,
                  }}>
                    {current.user.name?.[0]?.toUpperCase() ?? '?'}
                  </span>
                )}
                <b style={{ color: '#fff', fontWeight: 500 }}>{current.user.name}</b>
              </span>
              <span aria-hidden style={{ opacity: 0.5 }}>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>bookmark</span>
                {current.save_count}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>visibility</span>
                {current.view_count >= 1000 ? `${(current.view_count / 1000).toFixed(1)}k` : current.view_count}
              </span>
              <span aria-hidden style={{ opacity: 0.5, marginLeft: 'auto' }}>·</span>
              <span
                style={{
                  marginLeft: 'auto',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontWeight: 500, color: '#fff',
                }}
              >
                Open prompt
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_outward</span>
              </span>
            </div>
          </div>
        </button>

        {/* Prev / Next arrows — float on the right edge */}
        {len > 1 && (
          <div
            style={{
              position: 'absolute', top: '50%', right: 18,
              transform: 'translateY(-50%)',
              display: 'flex', flexDirection: 'column', gap: 8,
              zIndex: 2,
            }}
          >
            <ArrowButton dir="prev" onClick={prev} />
            <ArrowButton dir="next" onClick={next} />
          </div>
        )}

        {/* Dot indicators — bottom-center */}
        {len > 1 && (
          <div
            role="tablist"
            aria-label="Banner navigation"
            style={{
              position: 'absolute', bottom: 14, left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px',
              background: 'rgba(8, 10, 18, 0.45)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: 'var(--radius-full)',
              border: '1px solid rgba(255,255,255,0.14)',
              zIndex: 2,
            }}
          >
            {prompts.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === active}
                aria-label={`Go to slide ${i + 1} of ${len}`}
                onClick={() => setActive(i)}
                style={{
                  width: i === active ? 24 : 8,
                  height: 8,
                  borderRadius: 'var(--radius-full)',
                  background: i === active ? '#fff' : 'rgba(255, 255, 255, 0.45)',
                  border: 0,
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </div>
        )}

        {/* Ken-Burns keyframe lives here — keeps the carousel self-contained
            instead of polluting globals.css. */}
        <style jsx>{`
          @keyframes hero-kenburns {
            from { transform: scale(1); }
            to { transform: scale(1.06); }
          }
        `}</style>
      </section>

      {openPrompt && (
        <PromptDetailModal
          prompt={openPrompt}
          onClose={() => setOpenPrompt(null)}
        />
      )}
    </>
  );
}

function ArrowButton({ dir, onClick }: { dir: 'prev' | 'next'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === 'prev' ? 'Previous slide' : 'Next slide'}
      style={{
        width: 38, height: 38,
        display: 'grid', placeItems: 'center',
        borderRadius: '50%',
        background: 'rgba(8, 10, 18, 0.45)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        color: '#fff',
        cursor: 'pointer',
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(8, 10, 18, 0.65)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(8, 10, 18, 0.45)'; }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 20 }}>
        {dir === 'prev' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
      </span>
    </button>
  );
}
